import { useState, useCallback, useRef } from "react";
import { useAuthContext } from "hooks/useAuthContext";
import useRefreshToken from "hooks/useRefreshToken";
import {
    initiateChunkedUpload,
    uploadChunk,
    completeChunkedUpload,
    getUploadStatus,
    cancelChunkedUpload,
} from "api/services/chunked-upload-service";
import { ICompleteUploadResponse } from "api/models/response/ICompleteUploadResponse";
import { IApiError } from "api/models/IApiError";
import { ResourceType } from "models/ResourceType";

const MAX_RETRIES_PER_CHUNK = 5;
const RETRY_BASE_DELAY_MS = 1000;
const RETRY_MAX_DELAY_MS = 30000;

export type ChunkedUploadStatus =
    | "idle"
    | "initiating"
    | "uploading"
    | "completing"
    | "completed"
    | "failed"
    | "cancelled";

export interface ChunkedUploadProgress {
    uploadedChunks: number;
    totalChunks: number;
    percentage: number;
    currentChunk: number;
    currentChunkProgress: number;
}

export interface ChunkedUploadResult {
    success: boolean;
    data?: ICompleteUploadResponse;
    errors?: IApiError[];
}

export interface UseChunkedUploadReturn {
    upload: (
        file: File,
        resourceType: ResourceType,
        group?: string | null,
        onProgress?: (percentage: number) => void
    ) => Promise<ChunkedUploadResult>;

    resume: (
        sessionId: string,
        file: File,
        onProgress?: (percentage: number) => void
    ) => Promise<ChunkedUploadResult>;

    cancel: () => Promise<void>;
    status: ChunkedUploadStatus;
    progress: ChunkedUploadProgress;
    errors: IApiError[] | null;
    sessionId: string | null;
}

export const useChunkedUpload = (): UseChunkedUploadReturn => {
    const { userState } = useAuthContext();
    const { refreshToken } = useRefreshToken();

    const [status, setStatus] = useState<ChunkedUploadStatus>("idle");
    const [errors, setErrors] = useState<IApiError[] | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [progress, setProgress] = useState<ChunkedUploadProgress>({
        uploadedChunks: 0,
        totalChunks: 0,
        percentage: 0,
        currentChunk: -1,
        currentChunkProgress: 0,
    });

    const cancelledRef = useRef(false);

    const getWorkspaceId = useCallback(() => {
        return userState.user.selectedWorkspace.workspaceId;
    }, [userState.user.selectedWorkspace.workspaceId]);

    const backoff = useCallback(async (attempt: number) => {
        const delay = Math.min(
            RETRY_BASE_DELAY_MS * Math.pow(2, attempt) + Math.random() * 1000,
            RETRY_MAX_DELAY_MS
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
    }, []);

    const fail = useCallback((apiErrors?: IApiError[]): ChunkedUploadResult => {
        const errs = apiErrors?.length
            ? apiErrors
            : [{ code: "UnknownError", description: "Upload failed" }];
        setErrors(errs);
        setStatus("failed");
        return { success: false, errors: errs };
    }, []);

    const uploadChunkWithRetry = useCallback(
        async (
            workspaceId: string,
            sid: string,
            chunkIndex: number,
            chunkData: ArrayBuffer,
            totalChunks: number,
            uploadedSoFar: number,
            onProgress?: (percentage: number) => void
        ): Promise<{ success: boolean; errors?: IApiError[] }> => {
            for (let attempt = 0; attempt <= MAX_RETRIES_PER_CHUNK; attempt++) {
                if (cancelledRef.current) return { success: false };

                if (attempt > 0) {
                    await backoff(attempt - 1);
                    await refreshToken();
                }

                try {
                    const response = await uploadChunk(
                        workspaceId,
                        sid,
                        chunkIndex,
                        chunkData,
                        (progressEvent: any) => {
                            if (progressEvent.total) {
                                const chunkPercentage = Math.round(
                                    (progressEvent.loaded * 100) / progressEvent.total
                                );
                                const overallPercentage = Math.min(
                                    Math.round(((uploadedSoFar + chunkPercentage / 100) / totalChunks) * 100),
                                    99
                                );

                                setProgress((prev) => ({
                                    ...prev,
                                    currentChunk: chunkIndex,
                                    currentChunkProgress: chunkPercentage,
                                    percentage: overallPercentage,
                                }));
                                onProgress?.(overallPercentage);
                            }
                        }
                    );

                    if (response.success) {
                        return { success: true };
                    }

                    // 4xx = client error, don't retry — return backend errors
                    if (response.status >= 400 && response.status < 500) {
                        return { success: false, errors: response.errors };
                    }
                } catch {
                    // network error — will retry
                }
            }

            return {
                success: false,
                errors: [{ code: "UnknownError", description: "All upload retries exhausted" }],
            };
        },
        [backoff, refreshToken]
    );

    const uploadChunks = useCallback(
        async (
            workspaceId: string,
            sid: string,
            file: File,
            chunkSize: number,
            totalChunks: number,
            alreadyReceived: Set<number>,
            onProgress?: (percentage: number) => void
        ): Promise<{ success: boolean; errors?: IApiError[] }> => {
            let uploadedCount = alreadyReceived.size;

            for (let i = 0; i < totalChunks; i++) {
                if (cancelledRef.current) return { success: false };

                if (alreadyReceived.has(i)) continue;

                const start = i * chunkSize;
                const end = Math.min(start + chunkSize, file.size);
                const chunkBlob = file.slice(start, end);
                const chunkBuffer = await chunkBlob.arrayBuffer();

                setProgress((prev) => ({
                    ...prev,
                    currentChunk: i,
                    currentChunkProgress: 0,
                }));

                const result = await uploadChunkWithRetry(
                    workspaceId, sid, i, chunkBuffer, totalChunks, uploadedCount, onProgress
                );

                if (!result.success) {
                    return result;
                }

                uploadedCount++;
                const overallPercentage = Math.min(
                    Math.round((uploadedCount / totalChunks) * 100),
                    99
                );

                setProgress((prev) => ({
                    ...prev,
                    uploadedChunks: uploadedCount,
                    percentage: overallPercentage,
                }));
                onProgress?.(overallPercentage);
            }

            return { success: true };
        },
        [uploadChunkWithRetry]
    );

    const upload = useCallback(
        async (
            file: File,
            resourceType: ResourceType,
            group?: string | null,
            onProgress?: (percentage: number) => void
        ): Promise<ChunkedUploadResult> => {
            const workspaceId = getWorkspaceId();
            if (!workspaceId) {
                return fail([{ code: "NoWorkspace", description: "No workspace selected" }]);
            }

            cancelledRef.current = false;
            setStatus("initiating");
            setErrors(null);

            await refreshToken();

            const initResponse = await initiateChunkedUpload(workspaceId, {
                fileName: file.name,
                totalFileSize: file.size,
                resourceType,
                group,
            });

            if (!initResponse.success || !initResponse.data) {
                return fail(initResponse.errors);
            }

            const { sessionId: sid, chunkSizeBytes, totalChunks } = initResponse.data;
            setSessionId(sid);
            setProgress({
                uploadedChunks: 0,
                totalChunks,
                percentage: 0,
                currentChunk: -1,
                currentChunkProgress: 0,
            });
            setStatus("uploading");
            onProgress?.(0);

            const chunksResult = await uploadChunks(
                workspaceId, sid, file, chunkSizeBytes, totalChunks, new Set(), onProgress
            );

            if (!chunksResult.success) {
                if (cancelledRef.current) {
                    setStatus("cancelled");
                    return { success: false };
                }
                return fail(chunksResult.errors);
            }

            setStatus("completing");
            const completeResponse = await completeChunkedUpload(workspaceId, sid);

            if (!completeResponse.success || !completeResponse.data) {
                return fail(completeResponse.errors);
            }

            setProgress((prev) => ({ ...prev, percentage: 100 }));
            onProgress?.(100);
            setStatus("completed");

            return { success: true, data: completeResponse.data };
        },
        [getWorkspaceId, refreshToken, uploadChunks, fail]
    );

    const resume = useCallback(
        async (
            sid: string,
            file: File,
            onProgress?: (percentage: number) => void
        ): Promise<ChunkedUploadResult> => {
            const workspaceId = getWorkspaceId();
            cancelledRef.current = false;
            setStatus("initiating");
            setErrors(null);
            setSessionId(sid);

            await refreshToken();

            const statusResponse = await getUploadStatus(workspaceId, sid);
            if (!statusResponse.success || !statusResponse.data) {
                return fail(statusResponse.errors);
            }

            const { totalChunks, chunkSizeBytes, receivedChunks } = statusResponse.data;
            const received = new Set(receivedChunks);
            const initialPct = Math.round((received.size / totalChunks) * 100);

            setProgress({
                uploadedChunks: received.size,
                totalChunks,
                percentage: initialPct,
                currentChunk: -1,
                currentChunkProgress: 0,
            });
            setStatus("uploading");
            onProgress?.(initialPct);

            const chunksResult = await uploadChunks(
                workspaceId, sid, file, chunkSizeBytes, totalChunks, received, onProgress
            );

            if (!chunksResult.success) {
                if (cancelledRef.current) {
                    setStatus("cancelled");
                    return { success: false };
                }
                return fail(chunksResult.errors);
            }

            setStatus("completing");
            const completeResponse = await completeChunkedUpload(workspaceId, sid);

            if (!completeResponse.success || !completeResponse.data) {
                return fail(completeResponse.errors);
            }

            setProgress((prev) => ({ ...prev, percentage: 100 }));
            onProgress?.(100);
            setStatus("completed");

            return { success: true, data: completeResponse.data };
        },
        [getWorkspaceId, refreshToken, uploadChunks, fail]
    );

    const cancel = useCallback(async () => {
        cancelledRef.current = true;
        const workspaceId = getWorkspaceId();
        if (sessionId && workspaceId) {
            try {
                await cancelChunkedUpload(workspaceId, sessionId);
            } catch { }
        }
        setStatus("cancelled");
    }, [sessionId, getWorkspaceId]);

    return {
        upload,
        resume,
        cancel,
        status,
        progress,
        errors,
        sessionId,
    };
};